import { captureException } from '@sentry/nextjs';

export enum AgeType {
  Used = 'used',
  New = 'new',
  Classic = 'classic',
  NewPlus = 'newplus',
  None = 'none',
}

// logic ported from CG: https://code.cargurus.com/cargurus-eng/cargurus-frontend-utils/blob/main/packages/cargurus-utils/lib/helpers/checkYearWithinAgeRange.js

interface CheckYearParams {
  minAge?: number;
  maxAge?: number;
  cutOffYear?: number;
}

export const checkYearWithinAgeRange = (
  target: number,
  year: number,
  { minAge, maxAge, cutOffYear }: CheckYearParams
): boolean => {
  let meetsMaxReq = maxAge == null;
  let meetsMinReq = minAge == null;

  const meetsCutOffReq = cutOffYear != null ? year >= cutOffYear : true;

  const ageDelta = Math.abs(target - year);
  if (maxAge != null) {
    if (year >= target || ageDelta <= maxAge) {
      meetsMaxReq = true;
    }
  }
  if (minAge != null) {
    if (ageDelta >= minAge) {
      meetsMinReq = true;
    }
  }
  return meetsCutOffReq && meetsMaxReq && meetsMinReq;
};

const newCarCutOffYear = 2022;
const newPlusCarCutOffYear = newCarCutOffYear - 4;
const classicCarYearMinThreshold = 14;

// Logic ported from CG: https://code.cargurus.com/cargurus-eng/cargurus-shared-libraries-classic/blob/main/packages/listing-search-ads/src/domain/DfpAdTargeting.js#L129
export const targetingValueForSearchAgeRange = (
  searchYear: number | null | undefined
): AgeType[] => {
  if (!searchYear) {
    return [AgeType.None];
  }

  const currentYear = new Date().getFullYear();

  if (
    checkYearWithinAgeRange(currentYear, searchYear, {
      minAge: classicCarYearMinThreshold,
    })
  ) {
    return [AgeType.Classic];
  }
  const result = [];
  if (
    checkYearWithinAgeRange(currentYear, searchYear, {
      cutOffYear: newCarCutOffYear,
    })
  ) {
    result.push(AgeType.New);
  } else {
    result.push(AgeType.Used);
  }
  if (
    checkYearWithinAgeRange(currentYear, searchYear, {
      cutOffYear: newPlusCarCutOffYear,
    })
  ) {
    result.push(AgeType.NewPlus);
  }
  return result;
};

export function modifyAgeType(
  year: number | null | undefined,
  ageType: string | null | undefined
): string {
  try {
    const newAgeTypeTargeting = targetingValueForSearchAgeRange(year).toString();
    return newAgeTypeTargeting;
  } catch (e) {
    console.error('ERROR: adInfo modifyAgeType ', e);
    captureException(e);
    return ageType || '';
  }
}
